import {ReactNode, Suspense, useEffect, useRef} from "react";
import {Route, BrowserRouter as Router, Routes} from "react-router-dom";
import {Spinner, VStack} from "@chakra-ui/react";
import AppWrapper from "./components/layout/AppWrapper";
import Layout from "./components/layout";
// "/" is the first paint (it waits for the user then redirects): it stays in
// the main bundle, the other pages are loaded when they're first shown.
import CheckInformation from "./pages/CheckInformation";
import {NotificationListener} from "./components/layout/NotificationListener";
import ApprovalModal from "./components/layout/ApprovalModal";
import {
    CreateRequestProvider,
    NotificationProvider,
    useAuth,
} from "./context";
import {recordAppVisit} from "./helper/recentApps";
import {SessionProvider, lazyWithRetry} from "./shared/session";
import {sessionConfig} from "./session";

// the request list is where "/" sends everyone: its chunk is fetched while the
// user is being checked instead of after the redirect.
const loadRequestList = () => import("./pages/RequestList");
const RequestList = lazyWithRetry(loadRequestList);
const AddRequestPage = lazyWithRetry(() => import("./pages/AddRequest"));
const NoPage = lazyWithRetry(() => import("./pages/404"));

export function PageFallback() {
    return <VStack h="100dvb" w="full" justifyContent={"center"}>
        <Spinner />
    </VStack>;
}

function Lazy(props: {children: ReactNode}) {
    return <Suspense fallback={<PageFallback/>}>{props.children}</Suspense>;
}

// the pages render once the session is signed in (the spinner meanwhile, or
// while leaving for admiral's sign-in)
function PrivateRoute(props: {children: JSX.Element}) {
    return <SessionProvider config={sessionConfig} fallback={<PageFallback/>}>
        <NotificationProvider>
            <AppWrapper>
                <CreateRequestProvider>
                    <NotificationListener/>
                    <ApprovalModal/>
                    {props.children}
                </CreateRequestProvider>
            </AppWrapper>
        </NotificationProvider>
    </SessionProvider>;
}

function MainRoutes() {
    return <Routes>
        <Route path="/" element={<CheckInformation/>} />
        <Route
            path="/add-request"
            element={<Layout><Lazy><AddRequestPage/></Lazy></Layout>}
        />
        <Route
            path="/request-list"
            element={<Layout><Lazy><RequestList /></Lazy></Layout>}
        />
        <Route path="/*" element={<Lazy><NoPage/></Lazy>}/>
    </Routes>;
}

function App() {
    const hasFetched = useRef(false);
    const {status} = useAuth();
    // this app goes to the front of the shared "recently opened" history
    // (the sidebar shortcuts of every app read it)
    useEffect(() => {
        recordAppVisit();
        loadRequestList().catch(() => {});
    }, []);
    useEffect(function () {
        if (status === "authenticated" && !hasFetched.current) {
            hasFetched.current = true;
        }
        if (status === "unauthenticated") {
            hasFetched.current = false;
        }
    }, [status]);
    return <Router>
        <Routes>
            <Route path="/*" element={<PrivateRoute><MainRoutes/></PrivateRoute>}/>
        </Routes>
    </Router>;
}

export default App;
